import { useMemo } from 'react';
import { debounce } from '@mui/material';
import { EtablissementRead, SirenRead } from '@europrocurement/l2d-domain/openApi/ApiSiren';
import { useSirenService } from '@europrocurement/l2d-domain';
import { AddressFormatted } from './usePlaceAutocomplete';

export type CompanyType = AddressFormatted & {
    description: string;
    siren: string;
    siret: string;
    denomination: string;
    addressFull: string;
    civilite?: number;
    firstName: string;
    lastName: string;
    active?: boolean;
    principal?: boolean;
    activity?: string;
};

const useSirenSearch = () => {
    const { sirenApi } = useSirenService();

    const getEstablishmentAddress = (rawData: EtablissementRead) => {
        const fullAddressParts = [
            rawData?.address?.adresse,
            rawData?.address?.codePostal,
            rawData?.address?.commune,
            rawData?.address?.codePays,
        ].filter((part: string | null | undefined) => part !== null && part !== undefined);

        return {
            address: rawData?.address?.adresse as string,
            postalCode: rawData?.address?.codePostal as string,
            city: rawData?.address?.commune as string,
            countryCode: rawData?.address?.codePays
                ? rawData?.address?.codePays.substring(0, 2)
                : '',
            addressFull: fullAddressParts.join(', ') as string,
        };
    };

    const format = (result: SirenRead): Array<CompanyType> => {
        const etablissements = result?.etablissements || {
            principal: [],
            autres: [],
            suggestions: [],
        };
        const civilite = result?.civilite !== undefined && result.civilite === '1' ? 102 : 103;

        return (etablissements.suggestions || []).map((item) => {
            const identifier = item?.siret || result?.siren;
            const description = identifier
                ? `${result?.denomination} (${identifier})`
                : (result?.denomination as unknown as string);

            return {
                description,
                siren: result?.siren as string,
                siret: item?.siret as string,
                denomination: result?.denomination as string,
                civilite: result?.civilite !== undefined ? civilite : undefined,
                lastName: result?.nom as string,
                firstName: result?.prenom as string,
                active: item?.actif === true,
                principal: item?.indicateurEtablissementPrincipal === true,
                activity: item?.aActivite
                    ?.map((activity) => activity.descriptionDetaillee)
                    .join(', '),
                ...getEstablishmentAddress(item),
            };
        });
    };

    const searchBySiren = async (siren: string): Promise<Array<CompanyType>> =>
        new Promise((resolve, reject) => {
            sirenApi
                .apiRcsSirenGet({ siren })
                .then((response) => {
                    resolve(format(response.data));
                })
                .catch(() => {
                    reject();
                });
        });

    const fetchSuggestions = useMemo(
        () =>
            debounce(
                (
                    siren: string,
                    callback: (suggestions: CompanyType[], errorMessage?: string) => void,
                ) =>
                    searchBySiren(siren)
                        .then((results) => {
                            callback(results);
                        })
                        .catch(() => {
                            callback([], 'siren introuvable ou radié');
                        }),
                400,
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    return { fetchSuggestions };
};

export default useSirenSearch;
